/**
 * Authentication Store
 * 
 * Manages authentication state and user data using Zustand with persistence.
 * 
 * Token Storage:
 * - Tokens are stored in both Zustand state (via persist middleware) and localStorage
 * - Zustand persist automatically syncs state to localStorage under key 'auth-storage'
 * - Manual localStorage calls ensure immediate availability for code that reads directly
 * - This dual storage approach ensures compatibility with:
 *   - Axios interceptors that may read from localStorage as fallback
 *   - Other services that access tokens directly
 * 
 * Token Flow:
 * 1. After SSO login, tokens are stored via setAuth()
 * 2. Tokens are persisted to localStorage automatically (Zustand persist)
 * 3. Tokens are also set manually for immediate access
 * 4. Axios interceptors read tokens from store (with localStorage fallback)
 * 5. On logout, tokens are cleared from both store and localStorage
 * 
 * @see auth.service.ts for SSO login flow
 * @see interceptors.ts for token usage in API requests
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Decode the `department` claim out of a JWT access token without any
 * external dependency (kept local to avoid a circular import with interceptors.ts,
 * which also decodes tokens and itself calls into this store).
 */
const decodeDepartmentFromToken = (token: string): string => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return '';
        const payload = JSON.parse(atob(parts[1]));
        return payload.department ?? '';
    } catch {
        return '';
    }
};

/**
 * User interface
 */
export interface User {
    id: string;
    email: string;
    name: string;
    roles: string[];
    last_name?: string;
    first_name?: string;
    role?:string[]
}

/**
 * Authentication state interface
 */
interface AuthState {
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    setAuth: (user: User, accessToken: string, refreshToken: string) => void;
    clearAuth: () => void;
}

/**
 * Authentication store
 */
export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,

            /**
             * Sets authentication data
             * 
             * Stores user data and tokens in both Zustand state and localStorage.
             * 
             * Token Storage:
             * - Zustand persist middleware automatically stores state in localStorage
             * - Manual localStorage calls ensure immediate availability for:
             *   - Axios interceptors that read tokens directly
             *   - Other services that access tokens before store is initialized
             * 
             * After calling this:
             * - All subsequent API requests will include: Authorization: Bearer {accessToken}
             * - Tokens are available via useAuthStore.getState().accessToken
             * - Tokens are also available via localStorage.getItem('accessToken')
             * 
             * @param user - User data from SSO response
             * @param accessToken - JWT access token for API authentication
             * @param refreshToken - JWT refresh token for obtaining new access tokens
             */
            setAuth: (user, accessToken, refreshToken) => {
                // Store in localStorage for immediate access (before Zustand persist syncs)
                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', refreshToken);

                // Keep the logged-in user's department in sync in localStorage on every
                // login and token refresh (setAuth is called from both flows).
                const department = decodeDepartmentFromToken(accessToken);
                const topLevelDepartment = department.split('/')[0];
                if (topLevelDepartment) {
                    localStorage.setItem('department', topLevelDepartment);
                } else {
                    localStorage.removeItem('department');
                }

                // Store in Zustand state (persist middleware will sync to localStorage)
                set({
                    user,
                    accessToken,
                    refreshToken,
                    isAuthenticated: true,
                });
            },

            /**
             * Clears authentication data
             * 
             * Removes all authentication data from both Zustand state and localStorage.
             * This is called when:
             * - User logs out
             * - Token refresh fails
             * - Authentication is invalid
             * 
             * After calling this:
             * - All API requests will fail with 401 Unauthorized
             * - User must re-authenticate via SSO login
             */
            clearAuth: () => {
                // Clear localStorage tokens
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                localStorage.removeItem('department');
                
                // Clear Zustand state (persist middleware will sync to localStorage)
                set({
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    isAuthenticated: false,
                });
            },
        }),
        {
            name: 'auth-storage',
        }
    )
);